import React, { PropsWithChildren, useContext, useState } from 'react'

// Components
import * as Avatar from '../../Avatar'
import * as Content from '../../Content'
import { ItemProps, Label, Link } from './Item'
import { DrawerItem } from './DrawerItem'
import { NavigatorContext, NavigatorItemContext } from './NavigatorContext'

const SHARED_STYLES = 'flex my-auto mr-3 mb-auto ml-0 truncate'
const FONT_XSS = { fontSize: '0.625rem' }

const SECONDARY_LABEL_STYLES = `${SHARED_STYLES} font-body text-navcontentalt`

const CONTENTS_STYLES = `${SHARED_STYLES} font-body text-navcontent`

const CONTENT_WRAPPER_STYLES =
  'flex box-border m-0 flex-grow max-w-full min-w-0'

type ItemPrefix = 'draft' | 'cancelled'

export interface ContentItemProps extends ItemProps {
  content?: string
  secondaryLabel?: string
  imageUrl?: string
  hideAvatar?: boolean
  maxInitials?: number
  color?: string
  labelStyle?: React.CSSProperties
  itemPrefix?: ItemPrefix
  secondaryLabelStyle?: React.CSSProperties
  contentStyle?: React.CSSProperties
}

/**
 * An item for the primary navigation drawer representing actual
 * content such as a user profile in favor of a general navigation
 * section.
 * @param props Configuration for the item.
 */
export const ContentItem: React.FC<PropsWithChildren<ContentItemProps>> = ({
  active,
  color,
  contentStyle,
  to,
  label,
  itemPrefix,
  labelStyle,
  secondaryLabelStyle,
  maxInitials,
  children,
  content,
  imageUrl,
  hideAvatar = false,
  secondaryLabel,
}) => {
  const navigator = useContext(NavigatorContext)
  const [hovering, setHovering] = useState(false)
  const handleMouse = (over: boolean) => () => {
    if (hovering !== over) {
      setHovering(over)
    }
  }

  const colorForPrefix = () => {
    switch (itemPrefix) {
      case 'cancelled':
        return 'destructive'
      case 'draft':
        return 'warning'
      default:
        return undefined
    }
  }

  const defaultSecondaryLabelStyle: React.CSSProperties =
    navigator.theme === 'secondary'
      ? { fontSize: '0.95rem', lineHeight: 1.3 }
      : {}

  return (
    <DrawerItem
      appearanceTheme={navigator.theme}
      active={active}
      onMouseOver={handleMouse(true)}
      onMouseOut={handleMouse(false)}
    >
      <NavigatorItemContext.Provider
        value={{ active: active || false, hovering, theme: navigator.theme }}
      >
        <Link
          to={to}
          active={active}
          appearanceTheme={navigator.theme}
          renderCompact={navigator.compact}
        >
          {!hideAvatar ? (
            <Avatar.Simple
              className={'my-auto pointer-events-none'}
              color={color}
              name={label}
              imageUrl={imageUrl}
              size={42}
              maxInitials={maxInitials}
            />
          ) : null}
          <div className={CONTENT_WRAPPER_STYLES}>
            <div
              className={[
                'flex flex-col box-border m-0 w-full pointer-events-none flex-auto min-w-0',
                hideAvatar ? '' : 'pl-2',
              ].join(' ')}
            >
              <p className="flex box-border m-0">
                {itemPrefix ? (
                  <Content.Bubble bubbleStyle={colorForPrefix()}>
                    {itemPrefix}
                  </Content.Bubble>
                ) : null}
                <Label style={{ ...defaultSecondaryLabelStyle, ...labelStyle }}>
                  {label}
                </Label>
              </p>
              {secondaryLabel ? (
                <span
                  className={SECONDARY_LABEL_STYLES}
                  style={{ ...FONT_XSS, ...secondaryLabelStyle }}
                >
                  {secondaryLabel}
                </span>
              ) : null}
              {content ? (
                <span
                  className={CONTENTS_STYLES}
                  style={{ ...FONT_XSS, ...contentStyle }}
                >
                  {content}
                </span>
              ) : null}
            </div>
            {children}
          </div>
        </Link>
      </NavigatorItemContext.Provider>
    </DrawerItem>
  )
}
